/**
 * BulkCreateConsentMisPage Component
 *
 * Main page component for Bulk Create Consent MIS Dashboard
 * Displays metrics, filters, and batch listing for bulk consent creation operations
 *
 * Follows the same structure as BulkMisPage but for create_consent bulk action
 */

import React, { useState, useCallback, useMemo } from 'react';
import { BulkMisMetrics } from '../bulk-mis/BulkMisMetrics';
import { BulkMisFiltersComponent } from '../bulk-mis/BulkMisFilters';
import { BulkBatchRecordsDrawer } from '../bulk-mis/BulkBatchRecordsDrawer';
import { GenericBulkUploadDrawer } from '../bulk-mis/GenericBulkUploadDrawer';
import { useBulkCreateConsentData } from './hooks/useBulkCreateConsentData';
import { useBatchRecords } from '../bulk-mis/hooks/useBatchRecords';
import { Alert } from '../../ui/Alert';
import { Upload, Eye, RefreshCw } from 'lucide-react';
import { Button, DrawerProvider, Table, type TableColumn, Tag, Loader } from 'equal-ds-ui';
import { BULK_CREATE_CONSENT_CONFIG } from '../../../config/bulk-upload-configs';
import type { BulkMisFilters, BulkBatchRecord } from '../../../types/bulk-mis';
import type { PageComponentProps } from '../../../registry/react-component-registry.constants';

// Default pagination fallback - extracted to avoid creating new object on each render
const DEFAULT_PAGINATION = {
  page: 1,
  limit: 25,
  total_records: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
};

// Helper functions moved outside component to avoid recreation on each render
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const getProcessingTime = (batch: BulkBatchRecord): string => {
  const created = new Date(batch.created_at);
  const updated = new Date(batch.updated_at);
  const diffMs = updated.getTime() - created.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffSecs = Math.floor((diffMs % 60000) / 1000);

  if (diffMins > 60) {
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  } else if (diffMins > 0) {
    return `${diffMins}m ${diffSecs}s`;
  } else {
    return `${diffSecs}s`;
  }
};

export const BulkCreateConsentMisPage: React.FC<PageComponentProps> = () => {
  // Filters state
  const [filters, setFilters] = useState<BulkMisFilters>({
    search: '',
    status: 'all',
    dateRange: { from: null, to: null },
    pipelineSource: 'all',
    batchType: 'all',
  });

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Upload drawer state
  const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);

  // Records drawer state
  const [recordsDrawerOpen, setRecordsDrawerOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<BulkBatchRecord | null>(null);
  const [recordsPage, setRecordsPage] = useState(1);
  const [recordsPageSize, setRecordsPageSize] = useState(25);
  const [recordsStatusFilter, setRecordsStatusFilter] = useState('all');
  const [recordsSearch, setRecordsSearch] = useState('');
  const includeBalances = false; // Create consent doesn't need balance data

  // Fetch batches data with bulk_action = 'create_consent'
  const { data, loading, error, refresh } = useBulkCreateConsentData(filters, page, pageSize);

  // Fetch records for selected batch
  const {
    data: recordsData,
    loading: recordsLoading,
  } = useBatchRecords(
    selectedBatch?.reference_id || '',
    recordsPage,
    recordsPageSize,
    recordsStatusFilter,
    recordsSearch,
    includeBalances,
    'consent_request'
  );

  // Get status badge
  const getStatusBadge = (status: BulkBatchRecord['status']) => {
    const statusConfig = {
      COMPLETED: { label: 'Completed', className: 'bg-green-100 text-green-800' },
      PROCESSING: { label: 'In Progress', className: 'bg-yellow-100 text-yellow-800' },
      FAILED: { label: 'Failed', className: 'bg-red-100 text-red-800' },
      UPLOADED: { label: 'In Progress', className: 'bg-yellow-100 text-yellow-800' },
      IN_PROGRESS: { label: 'In Progress', className: 'bg-yellow-100 text-yellow-800' },
      READY: { label: 'In Progress', className: 'bg-yellow-100 text-yellow-800' },
    };

    const config = statusConfig[status] || statusConfig.IN_PROGRESS;

    return (
      <Tag className={config.className} size="sm">
        {config.label}
      </Tag>
    );
  };

  // Handlers
  const handleFiltersChange = (newFilters: BulkMisFilters) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page on filter change
  };

  const handleViewRecords = useCallback((batch: BulkBatchRecord) => {
    setSelectedBatch(batch);
    setRecordsDrawerOpen(true);
    setRecordsPage(1);
    setRecordsStatusFilter('all');
    setRecordsSearch('');
  }, []);

  // Table columns
  const columns: TableColumn<BulkBatchRecord>[] = useMemo(() => [
    {
      key: 'reference_id',
      header: 'Reference ID',
      width: 180,
      accessor: (row: BulkBatchRecord) => row.reference_id,
      render: (_value: string, row: BulkBatchRecord) => (
        <span className="font-mono text-sm text-gray-900" title={row.reference_id || 'N/A'}>
          {row.reference_id
            ? (row.reference_id.length > 20 ? `${row.reference_id.substring(0, 20)}...` : row.reference_id)
            : 'N/A'}
        </span>
      ),
    },
    {
      key: 'file_name',
      header: 'File Name',
      width: 200,
      accessor: (row: BulkBatchRecord) => row.file_name,
      render: (_value: string, row: BulkBatchRecord) => (
        <span className="text-sm text-gray-900" title={row.file_name}>
          {row.file_name.length > 30
            ? `${row.file_name.substring(0, 30)}...`
            : row.file_name}
        </span>
      ),
    },
    {
      key: 'pipeline_source',
      header: 'Source',
      width: 120,
      accessor: (row: BulkBatchRecord) => row.pipeline_source,
      render: (_value: string, row: BulkBatchRecord) => (
        <span className="text-sm text-gray-700 capitalize">{row.pipeline_source}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: 140,
      accessor: (row: BulkBatchRecord) => row.status,
      render: (_value: string, row: BulkBatchRecord) => getStatusBadge(row.status),
    },
    {
      key: 'total_records',
      header: 'Records',
      width: 120,
      accessor: (row: BulkBatchRecord) => row.total_records,
      render: (_value: number, row: BulkBatchRecord) => (
        <span className="text-sm text-gray-700">{row.total_records.toLocaleString()}</span>
      ),
    },
    {
      key: 'processing_time',
      header: 'Processing Time',
      width: 140,
      accessor: (row: BulkBatchRecord) => getProcessingTime(row),
      render: (_value: string, row: BulkBatchRecord) => (
        <span className="text-sm text-gray-700">{getProcessingTime(row)}</span>
      ),
    },
    {
      key: 'created_at',
      header: 'Created At',
      width: 180,
      accessor: (row: BulkBatchRecord) => row.created_at,
      render: (_value: string, row: BulkBatchRecord) => (
        <span className="text-sm text-gray-600">{formatDate(row.created_at)}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      isActions: true,
      width: 120,
      align: 'center',
      accessor: (row: BulkBatchRecord) => row.batch_id || row.reference_id,
      render: (_value: string, row: BulkBatchRecord) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleViewRecords(row);
          }}
          className="inline-flex items-center justify-center p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
          title="View Records"
          aria-label="View Records"
        >
          <Eye className="w-4 h-4" />
        </button>
      ),
    },
  ], [handleViewRecords]);

  return (
    <div className="equal-ds-root p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Bulk Create Consent MIS</h1>
        <p className="text-gray-600 mt-2">
          Monitor and analyze bulk consent creation operations
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="error" className="mb-6">
          {error}
        </Alert>
      )}

      {/* Metrics Section */}
      {data?.summary && <BulkMisMetrics summary={data.summary} />}

      {/* Upload Drawer */}
      <DrawerProvider open={uploadDrawerOpen} onOpenChange={setUploadDrawerOpen} side="right">
        <GenericBulkUploadDrawer
          config={BULK_CREATE_CONSENT_CONFIG}
          isOpen={uploadDrawerOpen}
          onClose={() => setUploadDrawerOpen(false)}
          onUploadSuccess={refresh}
        />
      </DrawerProvider>

      {/* Filters Section */}
      <BulkMisFiltersComponent
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />

      {/* Table Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        {/* Table Header with Upload and Export Buttons */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">All Batches</h2>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setUploadDrawerOpen(true)}
              variant="secondary"
              className="flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Upload Batch
            </Button>
            <Button
              onClick={refresh}
              variant="secondary"
              disabled={loading}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Table */}
        {data && (
          <Table
            data={data.batches}
            columns={columns}
            loading={loading}
            onRowClick={handleViewRecords}
            pagination={{
              currentPage: page,
              pageSize: pageSize,
              totalPages: data.pagination.total_pages,
              totalItems: data.pagination.total_records,
              onPageChange: (newPage) => setPage(newPage),
              onPageSizeChange: (newSize) => {
                setPageSize(newSize);
                setPage(1);
              },
              pageSizeOptions: [25, 50, 100],
            }}
          />
        )}

        {/* Loading State */}
        {loading && !data && (
          <div className="p-12">
            <div className="flex flex-col items-center justify-center gap-4">
              <Loader cellSize={12} />
              <p className="text-gray-600">Loading batches...</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && data && data.batches.length === 0 && (
          <div className="p-12 text-center">
            <p className="text-gray-500">No batches found</p>
          </div>
        )}
      </div>

      {/* Records Drawer */}
      {selectedBatch && (
        <DrawerProvider open={recordsDrawerOpen} onOpenChange={setRecordsDrawerOpen} side="right">
          <BulkBatchRecordsDrawer
            isOpen={recordsDrawerOpen}
            onClose={() => {
              setRecordsDrawerOpen(false);
              setSelectedBatch(null);
            }}
            batch={selectedBatch}
            records={recordsData?.records || []}
            pagination={recordsData?.pagination || DEFAULT_PAGINATION}
            loading={recordsLoading}
            summary={recordsData?.summary || undefined}
            showTabSwitcher={false}
            onPageChange={setRecordsPage}
            onPageSizeChange={(newSize) => {
              setRecordsPageSize(newSize);
              setRecordsPage(1);
            }}
            onStatusFilterChange={setRecordsStatusFilter}
            onSearchChange={setRecordsSearch}
          />
        </DrawerProvider>
      )}

    </div>
  );
};
